import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import React, { useState } from "react";
import { ORDER_TYPE } from "../../../../enums/order.enum";
import {
  getMuiTheme,
  parseAndFormateDate,
  textCapitalize,
} from "../../../../utils/common";
import AssignProviderModal from "./assignProviderModal";
import { UpdateNotesIcon } from "../../../../assets/icons";
import NotesModal from "../../notesModal";
import KnowlarityCall from "../../../Common/knowlarityCall";
import { commonTableOptions } from "../../../../constants";
import OrderBadges from "../../orderBadges";
import { ThemeProvider } from "@mui/styles";
import ErrorBoundary from "../../../Helpers/ErrorBoundary";

const PendingOrders: React.FC<{
  data: any;
  orderType: string;
  getOrders: any;
  dataLoading: boolean;
}> = ({ data, orderType, getOrders, dataLoading }) => {
  const [assignProviderModal, setAssignProviderModal] = useState(false);
  const [activeOrderId, setActiveOrderId] = useState("");
  const [activePartner, setActivePartner] = useState("");
  const [activePartnerOrderId, setActivePartnerOrderId] = useState("");
  const [notesModal, setNotesModal] = useState(false);

  const managePendingColumns = [
    {
      name: "partner_order_id",
      label: "PARTNER ORDER ID",
      options: {
        filter: false,
      },
    },
    {
      name: "customer_details.name",
      label: "CUSTOMER NAME",
      options: {
        filter: false,
      },
    },
    {
      name: "customer_details.mobile",
      label: "CUSTOMER MOBILE",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "consultation_type",
      label: "CONSULTATION DETAILS",
      options: {
        filter: orderType === ORDER_TYPE.CONSULTATION ? true : false,
        display: orderType === ORDER_TYPE.CONSULTATION ? true : false,
        searchable: false,
      },
    },
    {
      name: "sample_collection",
      label: "DIAGNOSTIC DETAILS",
      options: {
        filter: orderType === ORDER_TYPE.DIAGNOSTICS ? true : false,
        display: orderType === ORDER_TYPE.DIAGNOSTICS ? true : false,
        searchable: false,
      },
    },
    {
      name: "time_slot",
      label: "CONSULTATION TIME",
      options: {
        filter: false,
        display: false,
        searchable: false,
      },
    },
    {
      name: "order_partner",
      label: "ORDER PARTNER",
      options: {
        filter: true,
        //filterType: "textField" as FilterType,
        searchable: false,
      },
    },
    {
      name: "order_status",
      label: "ORDER STATUS",
      options: {
        filter: false,
        searchable: false,
      },
    },
    {
      name: "created_at",
      label: "CREATED AT",
      options: {
        filter: false,
        searchable: false,
      },
    },
    {
      name: "order_id",
      label: "ACTION",
      options: {
        filter: false,
        searchable: true,
      },
    },
    {
      name: "priority",
      label: "PRIORITY",
      options: {
        filter: true,
        display: false,
        searchable: false,
      },
    },
    {
      name: "express_delivery",
      label: "EXPRESS DELIVERY",
      options: {
        filter: orderType === ORDER_TYPE.MEDICINES ? true : false,
        display: false,
        searchable: false,
      },
    },
  ];

  const options: any = {
    textLabels: {
      body: {
        noMatch: dataLoading ? (
          <CircularProgress sx={{ color: "#0058ff" }} />
        ) : (
          "Sorry, there is no matching data to display"
        ),
      },
    },
    ...commonTableOptions,
    customRowRender: (data: any) => {
      return <CustomRowComponent key={data[0]} rowData={data} />;
    },
    searchPlaceholder: "Name/Partner Order ID/Order ID",
  };

  function CustomRowComponent({ rowData }: any) {
    const [
      partner_order_id,
      customer_name,
      customer_mobile,
      consultation_type,
      sample_collection,
      time_slot,
      order_partner,
      order_status,
      created_at,
      order_id,
      priority,
      express_delivery,
    ] = rowData;
    return (
      <TableRow hover>
        <TableCell
          sx={{
            maxWidth: { sm: "auto", md: "20vw" },
            overflow: "hidden",
            wordBreak: "break-all",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              variant="body2"
              sx={{ color: "#0058ff", cursor: "pointer" }}
              onClick={() => {
                setActivePartner(order_partner);
                setActivePartnerOrderId(partner_order_id);
                setAssignProviderModal(true);
              }}
            >
              {partner_order_id}
            </Typography>

            <OrderBadges
              priority={priority}
              order_status={order_status}
              partner_order_id={partner_order_id}
              express_delivery={express_delivery}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="column">
            <Stack direction="row" alignItems="center">
              <KnowlarityCall
                order={{ order_id: order_id, mobile: customer_mobile }}
                showNumber={false}
              />
              <Typography variant="body2">{customer_name}</Typography>
            </Stack>
          </Stack>
        </TableCell>
        {(orderType === ORDER_TYPE.CONSULTATION ||
          orderType === ORDER_TYPE.DIAGNOSTICS) && (
          <TableCell>
            <Stack>
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                {orderType === ORDER_TYPE.CONSULTATION
                  ? textCapitalize(consultation_type)
                  : textCapitalize(sample_collection?.split("_").join(" "))}
              </Typography>
              <Typography variant="body2" sx={{ color: "#718096" }}>
                {time_slot}
              </Typography>
            </Stack>
          </TableCell>
        )}
        <TableCell>
          <Typography variant="body2">{order_partner}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {textCapitalize(order_status?.split("_").join(" "))}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {parseAndFormateDate(created_at)}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row">
            <IconButton
              color="primary"
              sx={{ fontSize: "22px" }}
              onClick={() => {
                setActiveOrderId(order_id);
                setNotesModal(true);
              }}
            >
              <UpdateNotesIcon />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <ErrorBoundary>
      <Box>
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title=""
            data={data}
            columns={managePendingColumns}
            options={options}
          />
        </ThemeProvider>
        {assignProviderModal && (
          <AssignProviderModal
            assignProviderModal={assignProviderModal}
            setAssignProviderModal={setAssignProviderModal}
            activePartner={activePartner}
            activePartnerOrderId={activePartnerOrderId}
            assignType="assign"
          />
        )}
        {notesModal && (
          <NotesModal
            notesModal={notesModal}
            setNotesModal={setNotesModal}
            orderId={activeOrderId}
          />
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default PendingOrders;
